<template>
	<FormGroup :add-invalid-class="false">
		<FormGroup v-if="forCollective" class="mb-2">
			<RadioGroup
				v-model="collective"
				:options="collectiveOptions"
				stacked
				@input="emitChangeEvent"
			/>
		</FormGroup>
		<template v-if="salaryEligible">
			<div v-if="hint" class="mr-2 mb-1">{{ hint }}</div>
			<FormGroup
				:state="salaryInputValid"
				:invalid-feedback="salaryInputError && salaryInputError.show ? salaryInputError.text : ''"
				feedback-beneath
			>
				<template v-if="range">
					<div class="d-flex align-items-center">
						<div class="mr-1">Von</div>
						<b-input
							v-model="salaryInput"
							class="d-inline-block mr-1"
							style="width: 6rem"
							placeholder="zB: 2000"
							@input="emitChangeEvent"
						/>
						<div class="mr-1">bis</div>
						<b-input
							v-model="salaryInputMax"
							class="d-inline-block"
							style="width: 6rem"
							placeholder="zB: 2500"
							@input="emitChangeEvent"
						/>
					</div>
				</template>
				<template v-else>
					<div class="d-flex align-items-md-center flex-column flex-md-row">
						<div class="mr-4 d-flex align-items-center">
							<div>
								<b-input
									v-model="salaryInput"
									class="d-inline-block mr-1"
									style="width: 6rem"
									:placeholder="placeholderNonRange"
									@input="salaryInputChanged"
								/>
							</div>
							<div>&euro;</div>
						</div>
						<div class="flex-grow-1 mt-2 mt-md-0">
							<slot name="input-info" />
						</div>
					</div>
				</template>
			</FormGroup>
			<div v-if="withCalculatorLink" class="mt-3">
				Zum Brutto-Netto-Rechner:
				<a :href="calculatorLinks.AT" target="_blank">Österreich</a>
				| <a :href="calculatorLinks.DE" target="_blank">Deutschland</a>
				| <a :href="calculatorLinks.CH" target="_blank">Schweiz</a>
			</div>
		</template>
	</FormGroup>
</template>

<script>
import FormGroup from "@/vue/common/form/Group";
import {isDefined} from "@/helper/Value";
import RadioGroup from "@/vue/common/form/RadioGroup";
import {isEmptyOrNull} from "@/helper/String";
import {isPositiveInteger} from "@/helper/Number";
import Button from "@/vue/common/Button.vue";

const COLLECTIVE_NONE = 'none';
const COLLECTIVE_KNOWN = 'known';
const COLLECTIVE_UNKNOWN = 'unknown';

export default {

	components: {
		Button,
		RadioGroup,
		FormGroup,
	},

	props: {
		value: {
			type: Object,
			required: false,
		},
		range: {
			type: Boolean,
			required: false,
			default: false,
		},
		withCalculatorLink: {
			type: Boolean,
			required: false,
			default: true,
		},
		forCollective: {
			type: Boolean,
			required: false,
			default: false,
		},
		hint: {
			type: String,
			required: false,
		},
		inputInfo: {
			type: String,
			required: false,
			default: '&euro; (Brutto pro Monat)'
		},
		placeholderNonRange: {
			type: String,
			required: false,
			default: 'zB: 1750'
		}
	},

	mounted ()
	{
		this.emitChangeEvent();
	},

	data ()
	{
		return {
			collective: this.value?.collective,
			collectiveOptions: Object.freeze([
				{
					value: COLLECTIVE_UNKNOWN,
					text: 'Später eingeben'
				},
				{
					value: COLLECTIVE_NONE,
					text: 'Angabe eines Mindestgehalts für diesen Job nicht erforderlich'
				},
				{
					value: COLLECTIVE_KNOWN,
					text: 'Mindestgehalt für diesen Job beträgt (z.B. laut Kollektivvertrag / Tarifvertrag):'
				},
			]),
			salaryInput: this.value?.salary ? this.value.salary.toString() : '',
			salaryInputMax: this.value?.salaryMax ? this.value.salaryMax.toString() : '',
		};
	},

	computed: {
		calculatorLinks ()
		{
			return {
				AT: 'https://onlinerechner.haude.at/BMF-Brutto-Netto-Rechner/',
				DE: 'https://www.bundesfinanzministerium.de/Content/DE/Standardartikel/Service/Abgabenrechner/interaktiver-abgabenrechner.html',
				CH: 'https://www.lohncomputer.ch/de/rechner/ '
			};
		},
		collectiveValid ()
		{
			return !this.forCollective || isDefined(this.collective);
		},
		salaryEligible ()
		{
			if (!this.forCollective)
			{
				return true;
			}

			return !this.collective || this.collective === COLLECTIVE_KNOWN;
		},
		salaryInputValid ()
		{
			return !isDefined(this.salaryInputError);
		},
		salaryInputError ()
		{
			if (!this.salaryEligible)
			{
				return null;
			}

			if (this.range)
			{
				if (isEmptyOrNull(this.salaryInput) || isEmptyOrNull(this.salaryInputMax))
				{
					return {
						show: true,
						text: 'Bitte gib das Gehalt ein'
					};
				}

				if (!isPositiveInteger(this.salaryInput) || !isPositiveInteger(this.salaryInputMax))
				{
					return {
						show: true,
						text: 'Bitte gib nur Ziffern ein',
					};
				}

				if (parseInt(this.salaryInput) > parseInt(this.salaryInputMax))
				{
					return {
						show: true,
						text: 'Das Mindestgehalt darf nicht höher als das Maximalgehalt sein',
					};
				}
			}

			if (isEmptyOrNull(this.salaryInput))
			{
				return {
					show: false,
					text: 'Bitte gib das Gehalt ein'
				};
			}

			if (!isPositiveInteger(this.salaryInput))
			{
				return {
					show: true,
					text: 'Bitte gib nur Ziffern ein',
				};
			}

			return null;
		},
		valid ()
		{
			return this.collectiveValid
				&& this.salaryInputValid;
		},
	},

	methods: {
		salaryInputChanged()
		{
			if (this.forCollective && !this.collective)
			{
				this.collective = COLLECTIVE_KNOWN;
			}

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			const data = {
				collective: this.forCollective
					? this.collective
					: null,
				salary: this.salaryEligible ? this.salaryInput : null,
				salaryMax: this.salaryEligible && this.range ? this.salaryInputMax : null,
			};

			this.$emit('input', data);
		},
	}
}
</script>
